import React, { useState, useEffect } from 'react';
import { Building, Hotel, Briefcase, Home, Filter, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getProjects, getCategories } from '../api/api';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsData, categoriesData] = await Promise.all([
        getProjects(),
        getCategories()
      ]);
      setProjects(projectsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Icon mapping for categories
  const getIconComponent = (iconName) => {
    const icons = {
      'Briefcase': Briefcase,
      'Hotel': Hotel,
      'Home': Home,
      'Building': Building,
    };
    return icons[iconName] || Building;
  };

  // Build categories array dynamically from database
  const categoriesDisplay = [
    { id: 'all', label: 'All Projects', icon: Building, color: '#000000' },
    ...categories.map(cat => ({
      id: cat.slug,
      label: cat.name,
      icon: getIconComponent(cat.icon),
      color: cat.color
    }))
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  // Horizontal scroll function
  const scrollProjects = (direction) => {
    const container = document.getElementById('projects-scroll-container');
    if (container) {
      const scrollAmount = 400;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Open project modal
  const openProjectModal = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  // Close project modal
  const closeProjectModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  // Navigate images in modal
  const nextImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => 
        prev < selectedProject.images.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : selectedProject.images.length - 1
      );
    }
  };

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-4">
            Our <span className="text-yellow-600">Projects</span>
          </h2>
          <div className="w-24 h-1 bg-yellow-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore our diverse portfolio of over 40 completed projects across residential, commercial, hospitality, and institutional sectors.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categoriesDisplay.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-sm font-semibold transition-all duration-300 ${
                  activeFilter === cat.id
                    ? 'shadow-lg scale-105 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{
                  backgroundColor: activeFilter === cat.id ? cat.color : undefined
                }}
              >
                <Icon size={18} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Projects Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          {filteredProjects.length > 3 && (
            <>
              <button
                onClick={() => scrollProjects('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-6 h-6 text-black" />
              </button>
              <button
                onClick={() => scrollProjects('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-6 h-6 text-black" />
              </button>
            </>
          )}

          {/* Projects Container */}
          <div
            id="projects-scroll-container"
            className="flex gap-6 overflow-x-auto scroll-smooth pb-8 px-4"
            style={{ scrollbarWidth: 'thin' }}
          >
            {loading ? (
              <div className="w-full text-center py-12">
                <div className="inline-block w-12 h-12 border-4 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading projects...</p>
              </div>
            ) : (
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => openProjectModal(project)}
                  className="group bg-white rounded-sm shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex-shrink-0 cursor-pointer"
                  style={{ width: '380px' }}
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 right-4 bg-yellow-600 text-white text-xs font-semibold px-3 py-1 rounded-sm uppercase">
                      {project.category}
                    </div>

                    {/* Click to view hint */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="bg-yellow-600 text-white px-6 py-2 rounded-sm font-semibold">
                        Click to View Details
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-black mb-2 line-clamp-2">
                      {project.name}
                    </h3>
                    <p className="text-gray-600 mb-2 flex items-center gap-2">
                      <span className="w-1 h-1 bg-yellow-600 rounded-full"></span>
                      {project.location}
                    </p>
                    {project.area && (
                      <p className="text-sm text-gray-500">
                        Area: <span className="font-semibold">{project.area}</span>
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-12 text-center">
          <p className="text-lg text-gray-600 mb-4">
            Showing <span className="font-bold text-yellow-600">{filteredProjects.length}</span> projects
          </p>
          <div className="inline-block bg-gray-50 px-8 py-6 rounded-sm">
            <p className="text-3xl font-bold text-black mb-2">10,00,000+ sq.ft</p>
            <p className="text-gray-600">Total Area Designed & Delivered</p>
          </div>
        </div>

        {/* Project Detail Modal */}
        {selectedProject && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-sm max-w-5xl w-full max-h-[90vh] overflow-y-auto relative">
              {/* Close Button */}
              <button
                onClick={closeProjectModal}
                className="absolute top-4 right-4 z-20 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
              >
                <X className="w-6 h-6 text-black" />
              </button>

              {/* Image Gallery */}
              <div className="relative h-96 bg-gray-900">
                {selectedProject.images && selectedProject.images.length > 0 ? (
                  <>
                    <img
                      src={selectedProject.images[currentImageIndex]}
                      alt={`${selectedProject.name} - Image ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Image Navigation */}
                    {selectedProject.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                        >
                          <ChevronLeft className="w-6 h-6 text-black" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                        >
                          <ChevronRight className="w-6 h-6 text-black" />
                        </button>
                        
                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                          {currentImageIndex + 1} / {selectedProject.images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <img
                    src={selectedProject.image_url}
                    alt={selectedProject.name}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>

              {/* Project Details */}
              <div className="p-8">
                <div className="mb-4">
                  <span className="inline-block bg-yellow-600 text-white text-xs font-semibold px-3 py-1 rounded-sm uppercase mb-4">
                    {selectedProject.category}
                  </span>
                  <h2 className="text-3xl font-bold text-black mb-2">{selectedProject.name}</h2>
                  <p className="text-lg text-gray-600 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-600 rounded-full"></span>
                    {selectedProject.location}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {selectedProject.area && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-1">Area</h4>
                      <p className="text-lg text-black">{selectedProject.area}</p>
                    </div>
                  )}
                  {selectedProject.year && (
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-1">Year</h4>
                      <p className="text-lg text-black">{selectedProject.year}</p>
                    </div>
                  )}
                </div>

                {selectedProject.description && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 mb-2">Project Description</h4>
                    <p className="text-gray-600 leading-relaxed">{selectedProject.description}</p>
                  </div>
                )}

                {/* Thumbnail Gallery */}
                {selectedProject.images && selectedProject.images.length > 1 && (
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-700 mb-4">Gallery</h4>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                      {selectedProject.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`relative aspect-square rounded overflow-hidden border-2 transition-all ${
                            idx === currentImageIndex
                              ? 'border-yellow-600 scale-105'
                              : 'border-gray-200 hover:border-yellow-400'
                          }`}
                        >
                          <img
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;